import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import '../App.css';
import { fetchDeliveryJson, isLikelyFetchDropError } from '../utils/deliveryApiFetch';
import { ROS_ACTIVITY_OPTIONS, resolveActivity, manualEventErrors, newId, matchesSearch, activityTone, activityColor, sortRosEvents } from '../ros/rosActivity';
import { SharedProductionSection, ProductionResourcesSection, Collapse, productionSummaries, attentionText } from './MultiEventProduction';
import { issueLink, proposalAppUrl, rosRowOwner } from '../ros/sourceLinks';
import { newActivityForm, activityFormFrom, activityPayload, removalLabel, removalConfirmText, pendingActivityRow } from '../ros/activities';
import { HARD_TIME_TYPES, parseHardTime, formatHardTime, hardTimeErrors } from '../ros/hardTime';
import { savedLocations as savedLocationsOf, zoneForPlace } from '../ros/locations';
import { loadGoogleMapsApi, attachPlacesAutocomplete } from '../pricing/distance';
import { pickKey, memberKey, projectMemberIds, epsOnlyMember } from '../ros/members';
import { formatDisplayInstant } from '../utils/displayDate';

const AUTOSAVE_MS = 1500;
const GENERATE_POLL_MS = 15000;
const GENERATE_POLL_MAX_MS = 6 * 60 * 1000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Client draft of a ROS packet, from a saved record or the context defaults. */
export function draftFromContext(ctx) {
  const saved = ctx && ctx.rosPacket;
  if (saved) {
    const sd = saved.sharedDetails || {};
    // A title saved before titles were generated counts as the coordinator's
    // own unless it matches the generated one.
    const suggested = (ctx.preview && ctx.preview.suggestedTitle) || '';
    const titleCustom = sd.titleCustom === true ||
      (sd.titleCustom === undefined && !!saved.title && saved.title !== suggested);
    return {
      rosPacketId: saved.rosPacketId,
      title: saved.title || '',
      cityStateOverride: saved.cityStateOverride || '',
      dateRangeOverride: saved.dateRangeOverride || '',
      timeZone: saved.timeZone || '',
      selectedMembers: saved.selectedMembers || [],
      manualEvents: saved.manualEvents || [],
      customLocations: saved.customLocations || [],
      hiddenEpsEvents: saved.hiddenEpsEvents || [],
      sharedDetails: { ...sd, titleCustom }
    };
  }
  const d = (ctx && ctx.defaults) || {};
  return {
    rosPacketId: '',
    title: '',
    cityStateOverride: '',
    dateRangeOverride: '',
    timeZone: '',
    selectedMembers: d.selectedMembers || [],
    manualEvents: [],
    customLocations: [],
    hiddenEpsEvents: [],
    sharedDetails: { ...(d.sharedDetails || {}), titleCustom: false }
  };
}

function RunOfShowPanel({ deliveryApiUrl, userEmail, identity, ctx, onContext, embedded }) {
  const [draft, setDraft] = useState(() => draftFromContext(ctx));
  // Saves run one at a time; a change made while one is in flight is saved
  // next, carrying the packet ID the first save returned. A new packet is
  // created by the coordinator's first change, never by loading the editor.
  const saving = useRef({ inFlight: false, queued: null });
  const packetIdRef = useRef(draft.rosPacketId || '');
  const [saveStatus, setSaveStatus] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState('');
  const [showIssues, setShowIssues] = useState(false);
  const [editing, setEditing] = useState(null); // manual event being edited / added
  // Open sections for this visit; everything but the Run of Show starts closed.
  const [open, setOpen] = useState({});
  const autosave = useRef({ timer: null, dirty: false });
  const latestRef = useRef(null);
  const tripRef = useRef(null);
  const teamRef = useRef(null);

  const preview = ctx && ctx.preview;
  const rosPacket = ctx && ctx.rosPacket;
  const candidates = useMemo(() => (ctx && ctx.candidates) || [], [ctx]);
  const candidateById = useMemo(() => {
    const m = {};
    candidates.forEach((c) => { if (c.available) m[pickKey(c)] = c; });
    return m;
  }, [candidates]);
  const anchorId = ctx && ctx.anchor ? ctx.anchor.projectId : '';

  const update = (patch) => {
    autosave.current.dirty = true;
    setDraft((d) => ({ ...d, ...(typeof patch === 'function' ? patch(d) : patch) }));
  };

  // Default the time zone once from the EPS when the selected events agree.
  // For an unsaved packet this only fills the field; it is saved with the
  // coordinator's first change.
  useEffect(() => {
    if (preview && preview.suggestedTimeZone && !draft.timeZone) {
      if (packetIdRef.current) update({ timeZone: preview.suggestedTimeZone });
      else setDraft((d) => (d.timeZone ? d : { ...d, timeZone: preview.suggestedTimeZone }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview && preview.suggestedTimeZone]);

  // The ROS Title follows the generated "<Client> — <City> — <Dates>" until the
  // coordinator types their own; a custom title is never overwritten.
  const suggestedTitle = (preview && preview.suggestedTitle) || '';
  useEffect(() => {
    const custom = draft.sharedDetails && draft.sharedDetails.titleCustom;
    if (!suggestedTitle || custom || draft.title === suggestedTitle) return;
    if (packetIdRef.current) update({ title: suggestedTitle });
    else setDraft((d) => (d.sharedDetails && d.sharedDetails.titleCustom ? d : { ...d, title: suggestedTitle }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedTitle]);
  const setTitle = (value) => update((d) => ({
    title: value,
    sharedDetails: { ...(d.sharedDetails || {}), titleCustom: value.trim() !== '' && value.trim() !== suggestedTitle }
  }));

  const save = useCallback(async (d) => {
    const s = saving.current;
    if (s.inFlight) {
      s.queued = d;
      return;
    }
    s.inFlight = true;
    setSaveStatus('saving');
    try {
      const body = { ...d, rosPacketId: packetIdRef.current || d.rosPacketId || '' };
      const data = await fetchDeliveryJson(deliveryApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'saveRosPacket', ...identity, rosPacket: body, userEmail }),
        mode: 'cors'
      }, { retries: 2 });
      if (data && data.success) {
        setSaveStatus('saved');
        if (data.rosPacket && data.rosPacket.rosPacketId) {
          packetIdRef.current = data.rosPacket.rosPacketId;
          setDraft((cur) => (cur.rosPacketId ? cur : { ...cur, rosPacketId: data.rosPacket.rosPacketId }));
        }
        onContext(data);
      } else {
        setSaveStatus('error');
        setGenerateStatus((data && data.error) || 'Could not save the Multi-Event packet.');
      }
    } catch (_) {
      setSaveStatus('error');
    } finally {
      s.inFlight = false;
      if (s.queued) {
        const next = s.queued;
        s.queued = null;
        save(next);
      }
    }
  }, [deliveryApiUrl, identity, userEmail, onContext]);

  useEffect(() => {
    const a = autosave.current;
    if (!a.dirty || generating) return undefined;
    if (a.timer) clearTimeout(a.timer);
    a.timer = setTimeout(() => {
      a.dirty = false;
      save(draft);
    }, AUTOSAVE_MS);
    return () => { if (a.timer) clearTimeout(a.timer); };
  }, [draft, save, generating]);

  /* ---------- members ---------- */

  const selectedIds = draft.selectedMembers.map(memberKey);
  // Project-only parts (activities' projects, Production Information, resources) use these.
  const projectIds = projectMemberIds(draft.selectedMembers);
  const toggleMember = (id, on) => update((d) => {
    const ids = d.selectedMembers.map(memberKey);
    if (on) {
      const c = candidateById[id];
      if (c && c.epsOnly) return { selectedMembers: [...d.selectedMembers, epsOnlyMember(c)] };
      return ids.indexOf(id) === -1 ? { selectedMembers: [...d.selectedMembers, { projectId: id }] } : {};
    }
    if (id !== anchorId) return { selectedMembers: d.selectedMembers.filter((m) => memberKey(m) !== id) };
    return {};
  });

  /* ---------- manual events ---------- */

  const saveManual = (ev) => {
    update((d) => {
      let customLocations = d.customLocations;
      let locationRef = ev.locationRef;
      if (locationRef === 'new') {
        const loc = { id: newId(), name: ev.newLocationName.trim(), address: ev.newLocationAddress.trim() };
        customLocations = [...customLocations, loc];
        locationRef = `custom:${loc.id}`;
      }
      const clean = {
        id: ev.id,
        dateISO: ev.dateISO,
        time: ev.time.trim(),
        activity: resolveActivity(ev.activity, ev.customActivity),
        projectIds: ev.projectIds || [],
        project: '',
        locationRef,
        timeZone: ev.timeZone || '',
        hardTime: ev.hardTime.trim(),
        notes: ev.notes.trim(),
        createdAt: ev.createdAt || new Date().toISOString()
      };
      const exists = d.manualEvents.some((m) => m.id === ev.id);
      return {
        customLocations,
        manualEvents: exists
          ? d.manualEvents.map((m) => (m.id === ev.id ? clean : m))
          : [...d.manualEvents, clean]
      };
    });
    setEditing(null);
  };

  const deleteManual = (id) => update((d) => ({ manualEvents: d.manualEvents.filter((m) => m.id !== id) }));

  /* ---------- Production Activities (one shared record each) ---------- */

  const [activityBusy, setActivityBusy] = useState(false);
  const [activityError, setActivityError] = useState('');
  const activityRecords = () => ((ctx && ctx.preview && ctx.preview.activities) || []);
  const activityById = (id) => activityRecords().find((a) => a.id === id);
  const postActivity = (action, extra) => fetchDeliveryJson(deliveryApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({
      action, context: 'packet', ...identity, rosPacketId: packetIdRef.current || draft.rosPacketId || '',
      memberIds: projectIds, userEmail, ...extra
    }),
    mode: 'cors'
  }, { retries: 1 });

  // The Run of Show list the panel shows (a consolidated preview has rows; an older one events).
  const rowsKey = (p) => (p && p.rows ? 'rows' : 'events');
  const setPreview = (fn) => onContext((c) => (c && c.preview ? { ...c, preview: fn(c.preview) } : c));

  // As on the Standard page: the activity shows at once (marked Saving…) in its place, and the
  // saved packet's Run of Show from the reply replaces it. A failed save takes it out again and
  // reopens the form with the message.
  const saveActivity = async (form) => {
    const memberIds = projectIds;
    const payload = activityPayload(form, { memberIds, warehouseRef: ctx && ctx.warehouse && ctx.warehouse.ref });
    const members = (preview && preview.members) || [];
    const pending = pendingActivityRow(form, payload, { warehouse: ctx && ctx.warehouse, members });
    pending.project = payload.wholeOperation ? 'Whole operation'
      : payload.projectIds.map((id) => (members.find((m) => m.projectId === id) || {}).title || id).join('\n');
    const key = rowsKey(preview);
    const replaced = ((preview && preview[key]) || []).filter((e) => e.key === pending.key);
    setActivityError('');
    setActivityBusy(true);
    setEditing(null);
    setPreview((p) => ({ ...p, [key]: sortRosEvents([...(p[key] || []).filter((e) => e.key !== pending.key), pending]) }));
    const undo = (message) => {
      setPreview((p) => ({ ...p, [key]: sortRosEvents([...(p[key] || []).filter((e) => e.key !== pending.key), ...replaced]) }));
      setEditing(form);
      setActivityError(message);
    };
    try {
      const data = await postActivity('saveProductionActivity', { activity: payload, baseUpdatedAt: form.baseUpdatedAt || '' });
      if (data && data.success) {
        if (data.preview) setPreview(() => data.preview);
        else await refreshContext();
      } else {
        undo((data && data.error) || 'Could not save the activity.');
      }
    } catch (err) {
      undo(`${err.message || 'Could not save the activity.'} Nothing was saved — try again.`);
    } finally {
      setActivityBusy(false);
    }
  };

  const removeActivity = async (id) => {
    const rec = activityById(id);
    if (!rec || !window.confirm(removalConfirmText(rec, 'packet'))) return;
    setGenerateStatus('');
    try {
      const data = await postActivity('removeProductionActivity', { activityId: id, baseUpdatedAt: rec.updatedAt || '' });
      if (data && data.success) {
        if (data.preview) setPreview(() => data.preview);
        else await refreshContext();
      } else setGenerateStatus((data && data.error) || 'Could not remove the activity.');
    } catch (err) {
      setGenerateStatus(err.message || 'Could not remove the activity.');
    }
  };

  // A consolidated EPS row stands for several projects' rows: hide them all.
  const toggleHidden = (keys, hide) => update((d) => {
    const list = Array.isArray(keys) ? keys : [keys];
    return {
      hiddenEpsEvents: hide
        ? Array.from(new Set([...d.hiddenEpsEvents, ...list]))
        : d.hiddenEpsEvents.filter((k) => list.indexOf(k) === -1)
    };
  });

  /* ---------- generate ---------- */

  const applyGenerated = useCallback((data) => {
    const parts = [`Multi-Event packet v${data.version} generated${data.generatedAtHuman ? ` at ${data.generatedAtHuman}` : ''}`];
    if (data.companionFiles && data.companionFiles.length) {
      parts.push(`${data.companionFiles.length} companion floor-plan PDF${data.companionFiles.length === 1 ? '' : 's'} in the same folder`);
    }
    setGenerateStatus(`${parts.join(' · ')}.`);
    setTimeout(() => latestRef.current && latestRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, []);

  const refreshContext = useCallback(async () => {
    const params = new URLSearchParams({ action: 'getRosContext', ...identity });
    const data = await fetchDeliveryJson(`${deliveryApiUrl}?${params.toString()}`, undefined, { retries: 2 });
    if (data && data.success) onContext(data);
    return data;
  }, [deliveryApiUrl, identity, onContext]);

  const generatePart = (part, version) => fetchDeliveryJson(deliveryApiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify({
      action: 'generateRosPacket',
      ...identity,
      rosPacketId: packetIdRef.current || draft.rosPacketId,
      rosPacket: { ...draft, rosPacketId: packetIdRef.current || draft.rosPacketId },
      part,
      version,
      userEmail
    }),
    mode: 'cors'
  }, { retries: part ? 0 : 1 });

  const handleGenerate = async () => {
    const before = (rosPacket && rosPacket.latestVersion) || 0;
    if (autosave.current.timer) clearTimeout(autosave.current.timer);
    autosave.current.dirty = false;
    setGenerating(true);
    setGenerateStatus('Generating the Multi-Event packet with each project’s production details and floor plans — keep this tab open…');
    try {
      let data = await generatePart('', '');
      if (data.success && data.multipart) {
        // Oversized: the master packet first, then each companion floor-plan PDF.
        let master = null;
        for (let i = 0; i < data.parts.length; i++) {
          const part = data.parts[i];
          setGenerateStatus(part === 'master'
            ? 'Generating the master packet — keep this tab open…'
            : `Generating companion floor plans ${i} of ${data.parts.length - 1}…`);
          const res = await generatePart(part, data.version);
          if (!res.success) {
            setGenerateStatus(`Generation stopped at ${part === 'master' ? 'the master packet' : `companion file ${i}`}: ${res.error || 'Unknown error'}. Generate again to finish.`);
            await refreshContext();
            return;
          }
          if (part === 'master') master = res;
        }
        data = master;
      }
      if (data && data.success) {
        await refreshContext();
        applyGenerated(data);
      } else {
        setGenerateStatus(`Generation failed: ${(data && data.error) || 'Unknown error'}`);
      }
    } catch (err) {
      if (!isLikelyFetchDropError(err)) {
        setGenerateStatus(`Generation failed: ${err.message || 'Network error'}`);
      } else {
        setGenerateStatus('The browser lost connection while Google was still generating. Checking for a new version…');
        const deadline = Date.now() + GENERATE_POLL_MAX_MS;
        let done = false;
        while (!done && Date.now() < deadline) {
          await sleep(GENERATE_POLL_MS);
          try {
            const data = await refreshContext();
            const v = data && data.rosPacket ? data.rosPacket.latestVersion : 0;
            if (v > before) {
              applyGenerated({ version: v });
              done = true;
            }
          } catch (_) { /* keep polling */ }
        }
        if (!done) {
          setGenerateStatus('Generation may still be running, or it failed. Wait 2–3 minutes, refresh, and open this packet’s folder in Drive (Multi-Event Production Packets) and check its Archive.');
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  /* ---------- production information + resources ---------- */

  const [production, setProduction] = useState(null);
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionError, setProductionError] = useState('');
  const productionReq = useRef(0);
  const productionKey = projectIds.join(',');
  const loadProduction = useCallback(async () => {
    const req = ++productionReq.current;
    setProductionLoading(true);
    setProductionError('');
    try {
      const q = new URLSearchParams({ action: 'getRosProduction', ...identity, members: productionKey });
      const data = await fetchDeliveryJson(`${deliveryApiUrl}?${q.toString()}`, undefined, { retries: 1 });
      if (req !== productionReq.current) return;
      if (data && data.success) setProduction(data);
      else setProductionError((data && data.error) || 'Could not read the projects’ production details.');
    } catch (err) {
      if (req === productionReq.current) setProductionError(err.message || 'Network error');
    } finally {
      if (req === productionReq.current) setProductionLoading(false);
    }
  }, [deliveryApiUrl, identity, productionKey]);
  // Reload when membership changes, and after resource entries are saved.
  const resourcesKey = JSON.stringify((draft.sharedDetails && draft.sharedDetails.resources) || []);
  const savedResourcesKey = useRef(resourcesKey);
  useEffect(() => { loadProduction(); }, [loadProduction]);
  useEffect(() => {
    if (saveStatus === 'saved' && savedResourcesKey.current !== resourcesKey) {
      savedResourcesKey.current = resourcesKey;
      loadProduction();
    }
  }, [saveStatus, resourcesKey, loadProduction]);
  // The coordinator's "Refresh from EPS": re-read the EPS (Run of Show rows,
  // venues, Production Information) in place. A change still waiting to save
  // is saved first; nothing else in the draft changes.
  const [epsRefresh, setEpsRefresh] = useState({ status: '', at: '' });
  const refreshFromEps = async () => {
    if (epsRefresh.status === 'busy') return;
    setEpsRefresh({ status: 'busy', at: '' });
    const a = autosave.current;
    if (a.dirty) {
      if (a.timer) clearTimeout(a.timer);
      a.dirty = false;
      await save(draft);
    }
    try {
      // An unsaved packet is re-read from its selection (an EPS-only event by its row, as when it was added).
      const params = packetIdRef.current ? identity
        : { members: draft.selectedMembers.map((m) => (m.epsOnly ? `row:${m.rowNumber}@${m.loadInISO}` : m.projectId)).join(',') };
      const q = new URLSearchParams({ action: 'getRosContext', ...params });
      const [data] = await Promise.all([
        fetchDeliveryJson(`${deliveryApiUrl}?${q.toString()}`, undefined, { retries: 1 }),
        loadProduction()
      ]);
      if (data && data.success) {
        onContext(data);
        setEpsRefresh({ status: 'done', at: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) });
      } else {
        setEpsRefresh({ status: 'error', at: (data && data.error) || '' });
      }
    } catch (err) {
      setEpsRefresh({ status: 'error', at: err.message || '' });
    }
  };
  const setResourceEntries = (resources) => update((d) => ({ sharedDetails: { ...(d.sharedDetails || {}), resources } }));

  /* ---------- render ---------- */

  // Review items: blocking + warning. 'info' notes (already handled in what
  // prints, e.g. a next-day Hard Out) are not counted or listed.
  const issues = ((preview && preview.issues) || []).filter((i) => i.level !== 'info');
  // Each section's own review items, with a way to fix them where there is one.
  const epsUrl = ctx && ctx.epsUrl;
  const reviewBySection = {};
  issues.forEach((i) => {
    const sec = ISSUE_SECTION[i.code];
    if (!sec) return;
    // Fixed where the value is owned: the Proposal App for venue / address / day-of contact, the EPS for its own fields.
    const link = issueLink(i, epsUrl);
    const item = { code: i.code, text: i.message, href: link.href, linkLabel: link.label };
    reviewBySection[sec] = [...(reviewBySection[sec] || []), item];
  });
  const blocking = issues.filter((i) => i.level === 'blocking');
  const warnings = issues.filter((i) => i.level === 'warning');
  const memberInfo = (preview && preview.members) || [];
  // An activity is kept per project, so EPS-only events are not offered as its projects or venues.
  const projectMembers = memberInfo.filter((m) => !m.epsOnly);
  const venueOptions = projectMembers.filter((m) => m.venueName);
  // Inside the Production Packets wizard (step 03) the editor's parts are
  // sub-sections; on their own they keep the numbered section headers.
  const num = (n) => (embedded ? '' : n);

  const toggleOpen = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  // The 72-hour production gate, as the server worked it out for this packet.
  const gate = (preview && preview.gate) || null;
  const gateBlocked = !!(gate && gate.blocked);
  const gateItems = (sec) => (gate && gate.active ? (gate.missing || []).filter((i) => i.section === sec).map((i) => ({ code: i.code, text: i.text })) : []);
  const goToSection = (sec) => {
    setOpen((o) => ({ ...o, [sec]: true }));
    const el = sec === 'trip' ? tripRef.current : sec === 'team' ? teamRef.current : null;
    if (el && el.scrollIntoView) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  const memberClients = new Set(memberInfo.map((m) => String(m.client || m.title || '').toLowerCase()).filter(Boolean));
  // The events' own dates: warehouse travel days are left out of the range.
  const visibleRows = (preview && preview.events) || [];
  const onSite = visibleRows.filter((r) => !/Warehouse$/.test(r.activity || ''));
  const spanRows = onSite.length ? onSite : visibleRows;
  // Event dates, labelled: the packet's own range (Date Range above) can start
  // earlier, at the first warehouse departure.
  const eventsSummary = [
    `${selectedIds.length} project${selectedIds.length === 1 ? '' : 's'}`,
    memberClients.size ? `${memberClients.size} client${memberClients.size === 1 ? '' : 's'}` : '',
    spanRows.length ? `Events ${shortRange(spanRows[0].dateISO, spanRows[spanRows.length - 1].dateISO)}` : ''
  ].filter(Boolean).join(' · ');
  const prodSummary = productionSummaries(production, (preview && preview.locations) || []);
  const sd = draft.sharedDetails || {};
  const tripSummary = tripText(sd);
  const teamSummary = teamText(sd);

  return (
    <div className={`ros-panel${embedded ? ' ros-panel--embedded' : ''}`}>
      <div className="ros-eps-refresh" role="status">
        <span className="helper-text">
          {epsRefresh.status === 'busy' ? 'Reading the EPS…'
            : epsRefresh.status === 'done' ? `Updated from the EPS at ${epsRefresh.at}.`
              : epsRefresh.status === 'error' ? `Could not refresh from the EPS${epsRefresh.at ? ` — ${epsRefresh.at}` : ''}.`
                : 'Changed something on the EPS? Refresh to see it here.'}
        </span>
        <button type="button" className="pp-link-btn" onClick={refreshFromEps} disabled={epsRefresh.status === 'busy'}>
          Refresh from EPS
        </button>
      </div>
      <RosSection num={num('01')} title="Run of Show Settings" action={<SaveIndicator status={saveStatus} />}>
        <div className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label" htmlFor="ros-title">ROS Title</label>
            <input id="ros-title" type="text" className="input-field" value={draft.title}
              onChange={(e) => setTitle(e.target.value)} placeholder={suggestedTitle || 'Client — City — Dates'} />
            <span className="helper-text">
              {draft.sharedDetails && draft.sharedDetails.titleCustom
                ? `Your own title. Clear it to use “${suggestedTitle || 'Client — City — Dates'}”.`
                : 'Generated from the client, city and Run of Show dates. Type to use your own.'}
            </span>
          </div>
          <div className="form-section form-section-half">
            {preview && preview.mixedTimeZones ? (
              <>
                <span className="form-label">Time Zone</span>
                <div className="ros-static-value">Times display in the local time zone of each activity.</div>
              </>
            ) : (
              <>
                <label className="form-label" htmlFor="ros-tz">Time Zone</label>
                <select id="ros-tz" className="input-field" value={draft.timeZone}
                  onChange={(e) => update({ timeZone: e.target.value })}>
                  <option value="">Choose…</option>
                  {((ctx && ctx.timeZones) || []).map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
                </select>
              </>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label" htmlFor="ros-city">City / State</label>
            <input id="ros-city" type="text" className="input-field" value={draft.cityStateOverride}
              onChange={(e) => update({ cityStateOverride: e.target.value })}
              placeholder={(preview && preview.derivedCityState) || 'e.g. Charleston, South Carolina'} />
            <span className="helper-text">
              {preview && preview.derivedCityState
                ? `From the venue addresses: ${preview.derivedCityState}. Type to override.`
                : 'Fills in automatically when every venue is in the same city.'}
            </span>
          </div>
          <div className="form-section form-section-half">
            <label className="form-label" htmlFor="ros-dates">Date Range</label>
            <input id="ros-dates" type="text" className="input-field" value={draft.dateRangeOverride}
              onChange={(e) => update({ dateRangeOverride: e.target.value })}
              placeholder={(preview && preview.derivedDateRange) || 'Calculated from the Run of Show'} />
            <span className="helper-text">Calculated from the first and last Run of Show rows. Type only to override.</span>
          </div>
        </div>
      </RosSection>

      <Collapse title="Events Included" summary={eventsSummary}
        attention={attentionText(reviewBySection.events)} items={reviewBySection.events}
        open={open.events} onToggle={() => toggleOpen('events')}>
        <MemberPicker
          ctx={ctx}
          candidates={candidates}
          candidateById={candidateById}
          selectedIds={selectedIds}
          anchorId={anchorId}
          onToggle={toggleMember}
          events={(preview && preview.events) || []}
          labels={Object.fromEntries(memberInfo.map((m) => [m.projectId, m.title]))}
          memberInfo={memberInfo}
        />
      </Collapse>

      <RosSection num={num('03')} title="Run of Show">
        <RosTable
          events={(preview && (preview.rows || preview.events)) || []}
          hiddenKeys={draft.hiddenEpsEvents}
          epsUrl={ctx && ctx.epsUrl}
          onHide={toggleHidden}
          onEdit={(id) => {
            const m = draft.manualEvents.find((x) => x.id === id);
            if (m) setEditing(eventFormFrom(m));
          }}
          onDelete={deleteManual}
          onEditActivity={(id) => {
            const rec = activityById(id);
            setActivityError('');
            if (rec) setEditing(activityFormFrom(rec, ctx && ctx.warehouse && ctx.warehouse.ref, projectIds));
          }}
          onRemoveActivity={removeActivity}
          activityRemoveLabel={(id) => removalLabel(activityById(id), 'packet')}
        />
        {editing ? (
          <ManualEventForm
            value={editing}
            onChange={setEditing}
            onSave={editing.isActivity ? saveActivity : saveManual}
            onCancel={() => { setEditing(null); setActivityError(''); }}
            members={projectMembers}
            venueOptions={venueOptions}
            customLocations={editing.isActivity ? [] : draft.customLocations}
            savedLocations={editing.isActivity ? savedLocationsOf(activityRecords()) : []}
            warehouse={ctx && ctx.warehouse}
            timeZones={(ctx && ctx.timeZones) || []}
            allowWhole={!!editing.isActivity}
            saving={activityBusy}
            error={activityError}
          />
        ) : (
          <div className="form-section">
            <button type="button" className="pp-btn-tertiary" onClick={() => { setActivityError(''); setEditing(newActivityForm(projectIds)); }}>
              + Add Activity
            </button>
          </div>
        )}
      </RosSection>

      <RosSection num={num('04')} title="Production Information" className="ros-packet-section">
        <SharedProductionSection
          epsUrl={ctx && ctx.epsUrl}
          production={production}
          loading={productionLoading}
          error={productionError}
          deliveryApiUrl={deliveryApiUrl}
          identity={identity}
          userEmail={userEmail}
          memberIds={projectIds}
          onApplied={loadProduction}
          open={open}
          onToggle={toggleOpen}
        />
      </RosSection>

      {/* Packet-level sections: peers of Production Information, not part of any project. */}
      <div className="ros-packet-sections">
        <Collapse variant="section" title="Production Resources" summary={prodSummary.resources}
          attention={attentionText(reviewBySection.resources)} items={reviewBySection.resources}
          open={open.resources} onToggle={() => toggleOpen('resources')}>
        <ProductionResourcesSection
          production={production}
          loading={productionLoading}
          entries={(draft.sharedDetails && draft.sharedDetails.resources) || []}
          onEntries={setResourceEntries}
          deliveryApiUrl={deliveryApiUrl}
          identity={identity}
          userEmail={userEmail}
          memberIds={projectIds}
        />
        </Collapse>
        <div ref={tripRef}>
        <Collapse variant="section" title="Travel & Accommodations" summary={tripSummary}
          attention={gateAttention(gateItems('trip'))} items={gateItems('trip')}
          open={open.trip} onToggle={() => toggleOpen('trip')}>
          <SharedDetailsForm part="trip" value={draft.sharedDetails} onChange={(sharedDetails) => update({ sharedDetails })} />
        </Collapse>
        </div>
        <div ref={teamRef}>
        <Collapse variant="section" title="Delivery Team" summary={teamSummary}
          attention={gateAttention(gateItems('team'))} items={gateItems('team')}
          open={open.team} onToggle={() => toggleOpen('team')}>
          <SharedDetailsForm part="team" value={draft.sharedDetails} onChange={(sharedDetails) => update({ sharedDetails })} />
        </Collapse>
        </div>
      </div>

      <RosSection num={num('07')} title="Packet Readiness" className="ros-packet-section">
        <div ref={latestRef} className="pp-final ros-readiness">
          <div className="pp-final-row ros-generate-row">
            <div className="pp-final-status ros-review">
              {!preview ? (
                <span className="pp-status-line">Name this packet to start.</span>
              ) : issues.length === 0 ? (
                <span className="pp-status-ready">✓ Ready to generate</span>
              ) : (
                <>
                  <div className={`ros-review-count${blocking.length ? ' pp-status-warn' : ''}`}>
                    {issues.length} item{issues.length !== 1 ? 's' : ''} need{issues.length === 1 ? 's' : ''} review
                  </div>
                  {blocking.length > 0 && (
                    <div className="helper-text ros-review-help">{blocking.length} must be fixed before the packet can be generated.</div>
                  )}
                  <button type="button" className="me-action me-action--strong" aria-expanded={showIssues} onClick={() => setShowIssues((s) => !s)}>
                    {showIssues ? 'Hide items' : 'Review items →'}
                  </button>
                </>
              )}
              {/* Packet size is mentioned only when it changes what is generated. */}
              {production && production.plan && production.plan.summary.note && (
                <div className="helper-text ros-review-help" role="status">{production.plan.summary.note}</div>
              )}
            </div>
            <div className="pp-final-actions">
              <button type="button" className="btn-calculate pp-btn-emphasis" onClick={handleGenerate}
                disabled={generating || !preview || blocking.length > 0 || gateBlocked || saveStatus === 'saving'}>
                {generating ? (
                  <><span className="spinner-small"></span><span style={{ marginLeft: 8 }}>Generating…</span></>
                ) : (
                  <>{rosPacket && rosPacket.latestVersion > 0 ? 'Generate New Packet' : 'Generate Multi-Event Packet'} →</>
                )}
              </button>
            </div>
          </div>

          <GenerateGate gate={gate} onGo={goToSection} epsUrl={ctx && ctx.epsUrl} multi />
          {showIssues && issues.length > 0 && (
            <ul className="ros-issues">
              {blocking.map((i, idx) => <li key={`b${idx}`} className="ros-issue ros-issue--blocking"><strong>Must fix:</strong> {i.message}</li>)}
              {warnings.map((i, idx) => <li key={`w${idx}`} className="ros-issue">{i.message}</li>)}
            </ul>
          )}
          {generateStatus && <div className="helper-text" style={{ marginTop: 12 }}>{generateStatus}</div>}

          {rosPacket && rosPacket.latestVersion > 0 && (
            <div className="pp-final-row ros-current-packet">
              <div className="pp-final-info">
                <span className="pp-eyebrow">Current Multi-Event Packet</span>
                <div className="pp-final-when">
                  Version {rosPacket.latestVersion} · Generated {formatWhen(rosPacket.latestPacketGeneratedAt)}
                </div>
              </div>
              <div className="pp-final-actions">
                <a className="btn-secondary" style={{ textDecoration: 'none' }} href={rosPacket.latestPacketUrl}
                  target="_blank" rel="noopener noreferrer">View Packet</a>
                {driveDownloadUrl(rosPacket.latestPacketUrl) && (
                  <a className="btn-secondary" style={{ textDecoration: 'none' }} href={driveDownloadUrl(rosPacket.latestPacketUrl)}
                    target="_blank" rel="noopener noreferrer">Download PDF</a>
                )}
                {rosPacket.packetFolderId && (
                  <a className="btn-secondary" style={{ textDecoration: 'none' }}
                    href={`https://drive.google.com/drive/folders/${rosPacket.packetFolderId}`}
                    target="_blank" rel="noopener noreferrer">Open in Drive</a>
                )}
              </div>
            </div>
          )}
        </div>
      </RosSection>
    </div>
  );
}

/* ---------- pieces ---------- */

export function RosSection({ num, title, action, className, children }) {
  return (
    <section className={`pp-section${num ? '' : ' ros-subsection'}${className ? ` ${className}` : ''}`}>
      <div className="pp-section-head">
        {num && <span className="pp-section-num">{num}</span>}
        <h3 className="pp-section-title">{title}</h3>
        {action && <div className="pp-section-action">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function SaveIndicator({ status }) {
  if (status === 'saving') return <span className="pp-autosave">Saving…</span>;
  if (status === 'saved') return <span className="pp-autosave pp-autosave--ok">✓ Saved</span>;
  if (status === 'error') return <span className="pp-autosave pp-autosave--err">Save failed — your last change wasn't saved</span>;
  return null;
}

function EventCard({ c, children }) {
  return (
    <div className="ros-event-card">
      <div className="ros-event-title">{c.title}</div>
      <div className="ros-event-sub">
        {[c.dateText, c.venueName].filter(Boolean).join(' · ')}
      </div>
      <div className="ros-event-ref">{c.projectId ? `Project #${c.projectId}` : 'No Project ID'}</div>
      {children}
    </div>
  );
}

/**
 * Events Included, in the order they happen: each project's first Run of
 * Show row (else its Load-In), then name. Every project is named the same way
 * ("Client — Project"). The project the packet is opened from has no Remove;
 * that is all that sets it apart.
 */
export function MemberPicker({ ctx, candidates, candidateById, selectedIds, anchorId, onToggle, events, labels, memberInfo = [] }) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const programId = ctx && ctx.anchor ? ctx.anchor.programId : '';
  const programName = ctx && ctx.anchor ? ctx.anchor.programName : '';
  const firstDate = {};
  (events || []).forEach((e) => {
    (e.projectIds || []).forEach((pid) => {
      if (e.dateISO && (!firstDate[pid] || e.dateISO < firstDate[pid])) firstDate[pid] = e.dateISO;
    });
  });
  const nameOf = (pid) => (labels && labels[pid]) || (candidateById[pid] || {}).title || `Project #${pid}`;
  const dateOf = (pid) => firstDate[pid] || (candidateById[pid] || {}).loadInISO || '9999-12-31';
  const included = [...selectedIds].sort((a, b) => dateOf(a).localeCompare(dateOf(b)) || nameOf(a).localeCompare(nameOf(b)));
  const programExtras = programId
    ? candidates
      .filter((c) => c.programId === programId && c.projectId && c.available && selectedIds.indexOf(c.projectId) === -1 && !/cancel/i.test(c.proposalStatus))
      .sort((a, b) => String(a.loadInISO).localeCompare(String(b.loadInISO)))
    : [];
  const results = adding
    ? candidates.filter((c) => matchesSearch(c, query)).slice(0, 25)
    : [];

  return (
    <div className="ros-members">
      <div className="pp-group-label">Events</div>
      {included.map((pid) => {
        // An EPS-only event shows from the packet's own copy of its EPS row.
        const info = memberInfo.find((m) => m.projectId === pid && m.epsOnly);
        const c = info ? { projectId: '', title: info.title, dateText: info.dateText, venueName: info.venueName }
          : candidateById[pid] || { projectId: pid, title: `Project #${pid}`, dateText: 'Not found on the EPS' };
        return (
          <div key={`o-${pid}`} className="ros-member">
            <EventCard c={{ ...c, title: nameOf(pid) }}>
              {pid !== anchorId && (
                <button type="button" className="pp-link-btn" onClick={() => onToggle(pid, false)}>Remove</button>
              )}
            </EventCard>
          </div>
        );
      })}

      {programExtras.length > 0 && (
        <>
          <div className="pp-group-label">Also in {programId}{programName ? ` · ${programName}` : ''}</div>
          {programExtras.map((c) => (
            <div key={`p-${c.rowNumber}`} className="ros-member">
              <EventCard c={c}>
                <button type="button" className="pp-link-btn" onClick={() => onToggle(c.projectId, true)}>Add</button>
              </EventCard>
            </div>
          ))}
        </>
      )}

      {!adding ? (
        <div className="form-section">
          <button type="button" className="pp-btn-tertiary" onClick={() => setAdding(true)}>+ Add Another EPS Event</button>
        </div>
      ) : (
        <div className="ros-picker">
          <input type="search" className="input-field" autoFocus value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by client, event, venue, date (11/12, Nov 12) or project #" />
          <ul className="ros-picker-results" role="listbox">
            {results.length === 0 && <li className="helper-text">No matching EPS events.</li>}
            {results.map((c) => {
              const already = selectedIds.indexOf(pickKey(c)) !== -1 && c.available;
              return (
                <li key={`r-${c.rowNumber}`}>
                  <button type="button" className={`ros-picker-item${c.available ? '' : ' ros-member--off'}`}
                    disabled={!c.available || already}
                    title={c.available ? '' : c.reason}
                    onClick={() => { onToggle(pickKey(c), true); setQuery(''); setAdding(false); }}>
                    <EventCard c={c}>
                      {already && <div className="ros-event-ref">Already added</div>}
                      {!c.available && <div className="ros-event-reason">{c.reason}</div>}
                    </EventCard>
                  </button>
                </li>
              );
            })}
          </ul>
          <button type="button" className="pp-btn-tertiary" onClick={() => { setAdding(false); setQuery(''); }}>Done</button>
        </div>
      )}
    </div>
  );
}

/** "Hard Set 2:00 PM" reads as one unit: the time, then what it is (Hard Set, Hard Out, Deadline or a named time). */
function HardTime({ text }) {
  // EPS / proposal rows ("Hard Set …", "Hard Out … (next day)") read exactly as before.
  const m = String(text || '').match(/^(Hard (?:Set|Out))\s+(.+?)(\s*\(next day\))?$/i);
  const h = m ? { time: m[2], label: m[1], nextDay: !!m[3] } : (() => {
    const p = parseHardTime(text);
    return { time: p.time, label: p.type === 'Other' ? p.label : p.type, nextDay: p.nextDay };
  })();
  if (!h.time) return text ? <span className="ros-hard-free">{text}</span> : null;
  return (
    <span className="ros-hard">
      <span className="ros-hard-time">{h.time}</span>
      <span className="ros-hard-label">{h.label}{h.nextDay && <> · <span className="ros-hard-next">next day</span></>}</span>
    </span>
  );
}

/** The proposal behind an EPS row of exactly one project, else ''. */
function rowProposalUrl(e) {
  const ids = e.projectIds && e.projectIds.length ? e.projectIds : [e.projectId];
  return ids.length === 1 ? proposalAppUrl(ids[0]) : '';
}

// hideProject: a Standard packet's one project needs no Project column (nor RW #, its
// one RW ticket is in the packet's own details). RW # is reference only: blank when the EPS has none.
// Production Activity rows (e.store): Edit opens the shared activity; the removal button
// says what it does (activityRemoveLabel); the eye hides it from this packet only.
export function RosTable({ events, hiddenKeys, epsUrl, onHide, onEdit, onDelete, hideProject, onEditActivity, onRemoveActivity, activityRemoveLabel, onEditDeparture }) {
  // Hidden follows the coordinator's own list at once; the saved preview catches up.
  const hiddenSet = hiddenKeys ? new Set(hiddenKeys) : null;
  const isHidden = (e) => {
    if (!hiddenSet || !e.hideable) return !!e.hidden;
    const keys = e.keys || (e.key ? [e.key] : []);
    return keys.length > 0 && keys.every((k) => hiddenSet.has(k));
  };
  if (!events.length) {
    return <div className="pp-final-empty">No Run of Show rows yet.</div>;
  }
  return (
    <div className="ros-table-wrap">
      <table className={`ros-table ros-table--run${hideProject ? ' ros-table--single' : ''}`}>
        <colgroup>
          <col className="ros-col-date" />
          <col className="ros-col-time" />
          <col className="ros-col-activity" />
          {!hideProject && <col className="ros-col-project" />}
          {!hideProject && <col className="ros-col-rw" />}
          <col className="ros-col-venue" />
          <col className="ros-col-hard" />
          <col className="ros-col-notes" />
          <col className="ros-col-actions" />
        </colgroup>
        <thead>
          <tr><th>Date</th><th>Time</th><th>Activity</th>{!hideProject && <th>Project</th>}{!hideProject && <th>RW #</th>}<th>Venue</th><th>Hard Time</th><th>Notes</th><th aria-label="Actions" /></tr>
        </thead>
        <tbody>
          {events.map((row) => {
            const e = { ...row, hidden: isHidden(row) };
            return (
            <tr key={e.id} className={e.pending ? 'ros-row--pending' : e.hidden ? 'ros-row--hidden' : ''}>
              {/* A short muted bar on the left says what kind of activity this is; the name says it too. */}
              <td className={`ros-nowrap ros-tone ros-tone--${activityTone(e.activity)}`}
                style={{ '--ros-tone': activityColor(e.activity) }}>{rowDate(e.dateISO)}</td>
              <td className="ros-nowrap">{e.timeLabel || e.timeDisplay || 'TBD'}</td>
              <td>
                <span className="ros-activity-name">{e.activity}</span>
                {/* Where to change this row, as an action (secondary to the activity itself). */}
                <div className="ros-source">
                  {e.pending ? (
                    <span role="status">Saving…</span>
                  ) : e.source === 'eps' && e.epsOnly ? (
                    // An EPS-only event (no Project ID): every value, the time included, is on the EPS.
                    epsUrl ? <a href={epsUrl} target="_blank" rel="noopener noreferrer">Edit in EPS</a> : <>Edit in EPS</>
                  ) : e.source === 'eps' && (e.keys || [e.key]).some((k) => /:departure$/.test(k || '')) ? (
                    // The date is the EPS Departure Date (changed on the EPS only); the time is the
                    // project's Departure Time, set on its Standard packet.
                    <>{epsUrl ? <a href={epsUrl} target="_blank" rel="noopener noreferrer">Edit in EPS</a> : 'Edit in EPS'}
                      {' · '}{onEditDeparture
                        ? <button type="button" className="ros-source-edit" onClick={onEditDeparture}>Edit time</button>
                        : 'Edit time in the Standard packet'}</>
                  ) : e.source === 'eps' ? (
                    // EPS-owned rows are changed on the EPS; Load-In / Strike in the proposal.
                    rosRowOwner(e) === 'eps' ? (
                      epsUrl ? <a href={epsUrl} target="_blank" rel="noopener noreferrer">Edit in EPS</a> : <>Edit in EPS</>
                    ) : (
                      // One project's row opens its proposal; a row several projects share names where only.
                      rowProposalUrl(e)
                        ? <a href={rowProposalUrl(e)} target="_blank" rel="noopener noreferrer">Edit in Proposal App</a>
                        : <>Edit in Proposal App</>
                    )
                  ) : e.store ? (
                    <>{onEditActivity ? <button type="button" className="ros-source-edit" onClick={() => onEditActivity(e.activityId)}>Edit activity</button> : 'Activity'}
                      {/* Says what removing does: from this project / these projects, or deleting it. */}
                      {onRemoveActivity && <> · <button type="button" className="ros-source-edit" onClick={() => onRemoveActivity(e.activityId)}>
                        {activityRemoveLabel ? activityRemoveLabel(e.activityId) : 'Delete activity'}</button></>}
                      {e.outsideCount > 0 && <> · also on {e.outsideCount} other project{e.outsideCount === 1 ? '' : 's'}</>}
                    </>
                  ) : (
                    <><button type="button" className="ros-source-edit" onClick={() => onEdit(e.id.replace(/^manual:/, ''))}>Edit activity</button>
                      {onDelete && <> · <button type="button" className="ros-source-edit" onClick={() => onDelete(e.id.replace(/^manual:/, ''))}>Delete</button></>}
                    </>
                  )}
                  {e.hidden && <span className="ros-hidden-tag"> · Hidden</span>}
                </div>
              </td>
              {!hideProject && <td>{(e.projectLines && e.projectLines.length ? e.projectLines : [e.project]).map((line, i) => (
                <div key={i} className="ros-project-line">{line}</div>
              ))}</td>}
              {!hideProject && <td className="ros-rw">{e.rw || ''}</td>}
              <td>{e.venue}</td>
              <td><HardTime text={e.hardTime} /></td>
              <td>{e.notes}</td>
              <td className="ros-row-actions">
                {e.hideable && onHide && !e.pending && (
                  // Leaves the row out of this packet only; the EPS, proposal and activity are not changed.
                  <button type="button" className="ros-eye" onClick={() => onHide(e.keys || [e.key], !e.hidden)}
                    title={e.hidden ? 'Hidden from ROS' : 'Included in ROS'}
                    aria-label={`${e.hidden ? 'Hidden from ROS' : 'Included in ROS'}: ${e.activity} ${rowDate(e.dateISO)}`}
                    aria-pressed={!e.hidden}>
                    {e.hidden ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** "2:30 PM" → { hour: '2', minute: '30', meridiem: 'PM' }; blank or unreadable → TBD. */
export function splitClock(text) {
  const m = String(text || '').trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp])\.?\s*[Mm]?\.?/);
  if (!m) return { hour: '', minute: '', meridiem: '' };
  const h = parseInt(m[1], 10);
  if (!(h >= 1 && h <= 12)) return { hour: '', minute: '', meridiem: '' };
  return { hour: String(h), minute: m[2] || '00', meridiem: m[3].toUpperCase() === 'P' ? 'PM' : 'AM' };
}

/** The Run of Show's own time text: "2:30 PM", or '' for TBD. */
export function joinClock({ hour, minute, meridiem }) {
  return hour ? `${hour}:${minute || '00'} ${meridiem || 'AM'}` : '';
}

const CLOCK_MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

/** Hour : Minute AM/PM, or TBD (no hour). Same control in Standard and Multi-Event. */
export function TimePicker({ value, onChange, labelledBy, ariaPrefix }) {
  const aria = (name) => (ariaPrefix ? `${ariaPrefix} ${name.toLowerCase()}` : name);
  const t = splitClock(value);
  const minutes = t.minute && CLOCK_MINUTES.indexOf(t.minute) === -1 ? [...CLOCK_MINUTES, t.minute].sort() : CLOCK_MINUTES;
  const put = (patch) => {
    const next = { ...t, ...patch };
    if (patch.hour && !t.hour) { next.minute = next.minute || '00'; next.meridiem = next.meridiem || 'AM'; }
    onChange(joinClock(next));
  };
  return (
    <div className="ros-time-picker" role="group" aria-labelledby={labelledBy}>
      <select className="input-field" aria-label={aria('Hour')} value={t.hour} onChange={(e) => put({ hour: e.target.value })}>
        <option value="">TBD</option>
        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="ros-time-sep" aria-hidden="true">:</span>
      <select className="input-field" aria-label={aria('Minute')} value={t.minute} disabled={!t.hour} onChange={(e) => put({ minute: e.target.value })}>
        {!t.hour && <option value="">--</option>}
        {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select className="input-field" aria-label={aria('AM or PM')} value={t.meridiem} disabled={!t.hour} onChange={(e) => put({ meridiem: e.target.value })}>
        {!t.hour && <option value="">--</option>}
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export function emptyEventForm() {
  return {
    id: newId(),
    dateISO: '',
    time: '',
    activity: '',
    customActivity: '',
    projectIds: [],
    locationRef: '',
    timeZone: '',
    newLocationName: '',
    newLocationAddress: '',
    hardTime: '',
    notes: '',
    createdAt: ''
  };
}

function eventFormFrom(m) {
  const predefined = ROS_ACTIVITY_OPTIONS.indexOf(m.activity) !== -1;
  return {
    ...emptyEventForm(),
    ...m,
    activity: predefined ? m.activity : (m.activity ? 'Other' : ''),
    customActivity: predefined ? '' : (m.activity || ''),
    // Older saved events have one projectId; it reads as that one project.
    projectIds: Array.isArray(m.projectIds) ? m.projectIds : (m.projectId ? [m.projectId] : [])
  };
}

/** The Address field: typed, or picked with the Google Places autocomplete the Delivery Fee Calculator uses. */
function AddressInput({ id, value, onText, onPlace }) {
  const ref = useRef(null);
  const placeRef = useRef(onPlace);
  placeRef.current = onPlace;
  useEffect(() => {
    let detach = () => {};
    let live = true;
    // Without a key or the network the field stays a plain text field.
    loadGoogleMapsApi().then(() => {
      if (live) detach = attachPlacesAutocomplete(ref.current, { onPlace: (text, place) => placeRef.current(text, place) });
    }).catch(() => {});
    return () => { live = false; detach(); };
  }, []);
  return (
    <input id={id} ref={ref} type="text" className="input-field" value={value} autoComplete="off"
      onChange={(e) => onText(e.target.value)} placeholder="Start typing the address" />
  );
}

/** Hard Time as parts (type, time, next day), stored as the Run of Show's own text. */
function HardTimeFields({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="ros-form-row">
      <div className="ros-form-field ros-form-field--type">
        <label className="form-label" htmlFor="ros-ev-hardtype">Hard Time</label>
        <select id="ros-ev-hardtype" className="input-field" value={value.type}
          onChange={(e) => set(e.target.value ? { type: e.target.value } : { type: '', label: '', time: '', nextDay: false })}>
          <option value="">None</option>
          {HARD_TIME_TYPES.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
      {value.type === 'Other' && (
        <div className="ros-form-field">
          <label className="form-label" htmlFor="ros-ev-hardlabel">Name</label>
          <input id="ros-ev-hardlabel" type="text" className="input-field" value={value.label}
            onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Doors open" />
        </div>
      )}
      {value.type && (
        <div className="ros-form-field ros-form-field--time">
          <span className="form-label" id="ros-ev-hardtime">Time</span>
          <TimePicker labelledBy="ros-ev-hardtime" ariaPrefix="Hard time" value={value.time} onChange={(time) => set({ time, nextDay: time ? value.nextDay : false })} />
        </div>
      )}
      {value.type && value.time && (
        <label className="ros-form-check">
          <input type="checkbox" checked={!!value.nextDay} onChange={(e) => set({ nextDay: e.target.checked })} />
          Next day
        </label>
      )}
    </div>
  );
}

/**
 * The Run of Show activity editor (Standard and Multi-Event): when, what, where,
 * hard time, notes. A known location supplies its own time zone; a created one
 * keeps the zone it was saved with; a new address suggests one.
 * savedLocations: locations created on this view's activities (locations.js).
 */
export function ManualEventForm({ value, onChange, onSave, onCancel, members, venueOptions, customLocations = [], savedLocations = [], warehouse, timeZones = [], single, allowWhole, saving, error }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const saved = savedLocations.find((l) => l.ref === value.locationRef);
  // A warehouse or EPS venue gives the activity its local time zone; otherwise the activity carries one.
  const placeZone = value.locationRef === (warehouse && warehouse.ref)
    ? warehouse.timeZone
    : value.locationRef.indexOf('eps:') === 0
      ? ((members.find((m) => `eps:${m.projectId}` === value.locationRef) || {}).timeZone || '')
      : '';
  const needsTimeZone = !!String(value.time || '').trim() && !placeZone;
  const hard = value.hard || parseHardTime(value.hardTime);
  const errors = [...manualEventErrors(value, { needsTimeZone }), ...hardTimeErrors(hard)];
  const [touched, setTouched] = useState(false);
  const showOther = /^other$/i.test(value.activity);
  const zoneNote = placeZone ? 'From the venue'
    : saved && value.timeZone === saved.timeZone && saved.timeZone ? `From ${saved.name}`
      : value.zoneFrom === 'address' && value.timeZone ? 'From the address' : '';

  const chooseLocation = (ref) => {
    const loc = savedLocations.find((l) => l.ref === ref);
    if (loc) {
      onChange({ ...value, locationRef: ref, newLocationName: loc.name, newLocationAddress: loc.address, timeZone: loc.timeZone || value.timeZone, zoneFrom: '' });
    } else if (ref === 'new') {
      // A zone that came with a created location does not carry over to a new one.
      const carried = saved && value.timeZone === saved.timeZone;
      onChange({ ...value, locationRef: ref, newLocationName: '', newLocationAddress: '', zoneFrom: '', ...(carried ? { timeZone: '' } : {}) });
    } else {
      onChange({ ...value, locationRef: ref, zoneFrom: '' });
    }
  };

  return (
    <div className="ros-event-form" role="group" aria-label={value.isActivity ? 'Production Activity' : 'Run of Show event'}>
      {/* When */}
      <div className="ros-form-row">
        <div className="ros-form-field ros-form-field--date">
          <label className="form-label" htmlFor="ros-ev-date">Date *</label>
          <input id="ros-ev-date" type="date" className="input-field" value={value.dateISO}
            onChange={(e) => set('dateISO', e.target.value)} />
        </div>
        <div className="ros-form-field ros-form-field--time">
          <span className="form-label" id="ros-ev-time">Time</span>
          <TimePicker labelledBy="ros-ev-time" value={value.time} onChange={(t) => set('time', t)} />
        </div>
        <div className="ros-form-field ros-form-field--zone">
          <label className="form-label" htmlFor="ros-ev-tz">Time Zone{needsTimeZone ? ' *' : ''}</label>
          <select id="ros-ev-tz" className="input-field" value={placeZone || value.timeZone || ''} disabled={!!placeZone}
            onChange={(e) => onChange({ ...value, timeZone: e.target.value, zoneFrom: '' })}>
            <option value="">{needsTimeZone ? 'Choose…' : '—'}</option>
            {timeZones.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
          </select>
          {zoneNote && <span className="helper-text">{zoneNote}</span>}
        </div>
      </div>
      {/* What */}
      <div className="ros-form-row">
        <div className="ros-form-field">
          <label className="form-label" htmlFor="ros-ev-activity">Activity *</label>
          <input id="ros-ev-activity" list="ros-activity-options" className="input-field" value={value.activity}
            onChange={(e) => {
              const activity = e.target.value;
              // A return to the warehouse happens at the warehouse.
              const toWarehouse = /^return to warehouse$/i.test(activity) && !value.locationRef && warehouse;
              onChange({ ...value, activity, ...(toWarehouse ? { locationRef: warehouse.ref } : {}) });
            }} placeholder="Pick or type an activity" />
          <datalist id="ros-activity-options">
            {ROS_ACTIVITY_OPTIONS.map((a) => <option key={a} value={a} />)}
          </datalist>
        </div>
        {showOther && (
          <div className="ros-form-field">
            <label className="form-label" htmlFor="ros-ev-custom">Activity name *</label>
            <input id="ros-ev-custom" type="text" className="input-field" value={value.customActivity}
              onChange={(e) => set('customActivity', e.target.value)} placeholder="What is happening?" />
          </div>
        )}
      </div>
      {/* Where (and, in a Multi-Event packet, which projects) */}
      <div className="ros-form-row">
        {single ? null : value.isActivity ? (
        <div className="ros-form-field">
          <span className="form-label" id="ros-ev-applies">Applies to</span>
          <div className="me-applyto" role="group" aria-labelledby="ros-ev-applies">
            <label className="me-applyto-option">
              <input type="radio" name="ros-ev-scope" checked={value.scope === 'all'}
                onChange={() => onChange({ ...value, scope: 'all', projectIds: members.map((m) => m.projectId) })} />
              All projects
            </label>
            <label className="me-applyto-option">
              <input type="radio" name="ros-ev-scope" checked={value.scope === 'chosen'}
                onChange={() => onChange({ ...value, scope: 'chosen', projectIds: value.projectIds.length ? value.projectIds : (members.length ? [members[0].projectId] : []) })} />
              Chosen projects
            </label>
            {value.scope === 'chosen' && (
              <div className="me-applyto-list">
                {members.map((m) => (
                  <label key={m.projectId} className="me-applyto-check">
                    <input type="checkbox" checked={value.projectIds.indexOf(m.projectId) !== -1}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...value.projectIds, m.projectId]
                          : value.projectIds.filter((x) => x !== m.projectId);
                        set('projectIds', members.map((x) => x.projectId).filter((id) => next.indexOf(id) !== -1));
                      }} />
                    {m.title}
                  </label>
                ))}
              </div>
            )}
            {allowWhole && (
              <label className="me-applyto-option me-applyto-secondary">
                <input type="radio" name="ros-ev-scope" checked={value.scope === 'whole'}
                  onChange={() => onChange({ ...value, scope: 'whole' })} />
                Whole operation (this packet only)
              </label>
            )}
            {value.outsideCount > 0 && (
              <p className="helper-text">Also on {value.outsideCount} project{value.outsideCount === 1 ? '' : 's'} outside this packet; changes apply there too.</p>
            )}
          </div>
        </div>
        ) : (
        <div className="ros-form-field">
          <span className="form-label" id="ros-ev-applies">Applies to</span>
          <div className="me-applyto" role="group" aria-labelledby="ros-ev-applies">
            <label className="me-applyto-option">
              <input type="radio" name="ros-ev-scope" checked={!value.projectIds.length}
                onChange={() => set('projectIds', [])} />
              All projects
            </label>
            <label className="me-applyto-option">
              <input type="radio" name="ros-ev-scope" checked={value.projectIds.length > 0}
                onChange={() => set('projectIds', members.length ? [members[0].projectId] : [])} />
              Chosen projects
            </label>
            {value.projectIds.length > 0 && (
              <div className="me-applyto-list">
                {members.map((m) => (
                  <label key={m.projectId} className="me-applyto-check">
                    <input type="checkbox" checked={value.projectIds.indexOf(m.projectId) !== -1}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...value.projectIds, m.projectId]
                          : value.projectIds.filter((x) => x !== m.projectId);
                        set('projectIds', members.map((x) => x.projectId).filter((id) => next.indexOf(id) !== -1));
                      }} />
                    {m.title}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
        <div className="ros-form-field">
          <label className="form-label" htmlFor="ros-ev-location">Venue / Location</label>
          <select id="ros-ev-location" className="input-field" value={value.locationRef}
            onChange={(e) => chooseLocation(e.target.value)}>
            <option value="">No location</option>
            {warehouse && <option value={warehouse.ref}>{warehouse.name}</option>}
            {venueOptions.map((m) => (
              <option key={`eps:${m.projectId}`} value={`eps:${m.projectId}`}>{m.venueName} ({m.title})</option>
            ))}
            {savedLocations.map((l) => <option key={l.ref} value={l.ref}>{l.name}</option>)}
            {customLocations.map((l) => <option key={l.id} value={`custom:${l.id}`}>{l.name}</option>)}
            <option value="new">+ New location…</option>
          </select>
          {saved && saved.address && <span className="helper-text">{saved.address}</span>}
        </div>
      </div>
      {value.locationRef === 'new' && (
        <div className="ros-form-row">
          <div className="ros-form-field">
            <label className="form-label" htmlFor="ros-ev-locname">Location Name *</label>
            <input id="ros-ev-locname" type="text" className="input-field" value={value.newLocationName}
              onChange={(e) => set('newLocationName', e.target.value)} />
          </div>
          <div className="ros-form-field ros-form-field--wide">
            <label className="form-label" htmlFor="ros-ev-locaddr">Address *</label>
            <AddressInput id="ros-ev-locaddr" value={value.newLocationAddress}
              onText={(text) => set('newLocationAddress', text)}
              onPlace={(text, place) => {
                const zone = zoneForPlace(place);
                onChange({ ...value, newLocationAddress: text, ...(zone ? { timeZone: zone, zoneFrom: 'address' } : {}) });
              }} />
          </div>
        </div>
      )}
      {/* Constraint */}
      <HardTimeFields value={hard} onChange={(next) => onChange({ ...value, hard: next, hardTime: formatHardTime(next) })} />
      {/* Detail */}
      <div className="ros-form-row">
        <div className="ros-form-field ros-form-field--wide">
          <label className="form-label" htmlFor="ros-ev-notes">Notes</label>
          <input id="ros-ev-notes" type="text" className="input-field" value={value.notes}
            onChange={(e) => set('notes', e.target.value)} />
        </div>
      </div>
      {touched && errors.length > 0 && (
        <ul className="ros-issues">{errors.map((e) => <li key={e} className="ros-issue ros-issue--blocking">{e}</li>)}</ul>
      )}
      {error && <div className="helper-text pp-error" role="alert">{error}</div>}
      <div className="ros-form-actions">
        <button type="button" className="btn-secondary" disabled={!!saving}
          onClick={() => { setTouched(true); if (!errors.length) onSave(value); }}>
          {value.isActivity ? (saving ? 'Saving…' : 'Save Activity') : 'Save Event'}
        </button>
        <button type="button" className="pp-btn-tertiary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export function SharedDetailsForm({ value, onChange, part }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const personnel = Array.isArray(v.deliveryPersonnel) ? v.deliveryPersonnel : [];
  const showTrip = part !== 'team';
  const showTeam = part !== 'trip';
  const travel = v.overnightTravelRequired || '';
  const team = v.teamStructure || '';
  return (
    <>
      {showTrip && (<>
      <ChoiceRow id="sd-travel" label="Overnight travel required?" value={travel} onChange={(x) => set('overnightTravelRequired', x)}
        options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
      {travel === 'no' ? (
        <p className="helper-text sd-choice-note">No overnight travel: Travel &amp; Accommodations is left out of the packet.</p>
      ) : (<>
      <div className="form-row">
        <div className="form-section form-section-half">
          <label className="form-label" htmlFor="sd-hotelName">Hotel Name</label>
          <input id="sd-hotelName" type="text" className="input-field" value={v.hotelName || ''} onChange={(e) => set('hotelName', e.target.value)} />
        </div>
        <div className="form-section form-section-half">
          <label className="form-label" htmlFor="sd-hotelAddress">Hotel Address</label>
          <input id="sd-hotelAddress" type="text" className="input-field" value={v.hotelAddress || ''} onChange={(e) => set('hotelAddress', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-section form-section-half">
          <label className="form-label" htmlFor="sd-teamHotelConf">Confirmation #</label>
          <input id="sd-teamHotelConf" type="text" className="input-field" value={v.teamHotelConf || ''} onChange={(e) => set('teamHotelConf', e.target.value)} />
        </div>
        <div className="form-section form-section-half">
          <label className="form-label" htmlFor="sd-numberOfRooms"># of Rooms</label>
          <input id="sd-numberOfRooms" type="text" className="input-field" value={v.numberOfRooms || ''} onChange={(e) => set('numberOfRooms', e.target.value)} />
        </div>
      </div>
      </>)}
      </>)}
      {showTeam && (<>
      <ChoiceRow id="sd-team" label="Team Structure" value={team} onChange={(x) => set('teamStructure', x)}
        options={[{ value: 'supervisor_only', label: 'Supervisor only' }, { value: 'supervisor_team', label: 'Supervisor + team' }]} />
      <div className="form-row">
        <div className="form-section form-section-half">
          <label className="form-label" htmlFor="sd-supervisor">Supervisor</label>
          <input id="sd-supervisor" type="text" className="input-field" value={v.supervisor || ''} onChange={(e) => set('supervisor', e.target.value)} />
        </div>
      </div>
      {personnel.map((p, idx) => (
        <div key={idx} className="form-row">
          <div className="form-section form-section-half">
            <label className="form-label">Name</label>
            <input type="text" className="input-field" value={p.name || ''}
              onChange={(e) => set('deliveryPersonnel', personnel.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))} />
          </div>
          <div className="form-section form-section-half pp-personnel-role">
            <div style={{ flex: 1 }}>
              <label className="form-label">Role</label>
              <input type="text" className="input-field" value={p.role || ''} placeholder="Delivery Personnel"
                onChange={(e) => set('deliveryPersonnel', personnel.map((x, i) => (i === idx ? { ...x, role: e.target.value } : x)))} />
            </div>
            <button type="button" className="pp-btn-tertiary"
              onClick={() => set('deliveryPersonnel', personnel.filter((_, i) => i !== idx))}>Remove</button>
          </div>
        </div>
      ))}
      <div className="form-section">
        <button type="button" className="pp-btn-tertiary"
          onClick={() => set('deliveryPersonnel', [...personnel, { name: '', role: 'Delivery Personnel' }])}>
          + Add Personnel
        </button>
      </div>
      </>)}
    </>
  );
}

/** A section's collapsed line while the gate needs something there: "2 required to generate". */
export function gateAttention(items) {
  const n = (items || []).length;
  return n ? `${n} required to generate` : '';
}

/** One explicit answer (never inferred); nothing is chosen until the coordinator picks. */
function ChoiceRow({ id, label, value, options, onChange }) {
  return (
    <div className="form-section sd-choice">
      <span className="form-label" id={`${id}-label`}>{label}</span>
      <div className="me-applyto sd-choice-options" role="radiogroup" aria-labelledby={`${id}-label`}>
        {options.map((o) => (
          <label key={o.value} className="me-applyto-option">
            <input type="radio" name={id} checked={value === o.value} onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * What stops Generate New Packet, said beside the button. The server decides
 * (the 72-hour production gate); this only shows its answer. onGo(section)
 * opens the section an item belongs to ('trip' or 'team'). multi: a
 * Multi-Event packet, which names the events that started the window.
 */
export function GenerateGate({ gate, onGo, epsUrl, multi }) {
  if (!gate) return null;
  if (!gate.blocked) {
    return gate.startsText ? (
      <div className="helper-text pp-gate-upcoming">
        Travel and delivery team details are required from {gate.startsText} (72 hours before warehouse departure).
      </div>
    ) : null;
  }
  const missing = gate.missing || [];
  const goLabel = { trip: 'Travel & Accommodations →', team: 'Delivery Team →' };
  return (
    <div className="pp-gate" role="alert">
      {(gate.departureMissing || []).map((i) => (
        <div key={i.text} className="pp-gate-line">
          {i.text}
          {epsUrl && <> <a className="pp-link-btn" href={epsUrl} target="_blank" rel="noopener noreferrer">Open the EPS →</a></>}
        </div>
      ))}
      {gate.active && missing.length > 0 && (
        <>
          {multi && (gate.activeEvents || []).length ? (
            <>
              <div className="pp-gate-head">This packet is inside the 72-hour production window because:</div>
              <ul className="pp-gate-list">
                {gate.activeEvents.map((e) => <li key={e.label + e.departureText}>{e.label} — departs {e.departureText}</li>)}
              </ul>
              <div className="pp-gate-head">Complete before generating:</div>
            </>
          ) : (
            <div className="pp-gate-head">
              Complete {missing.length} production detail{missing.length === 1 ? '' : 's'} before generating this packet
              {gate.activeEvents && gate.activeEvents[0] ? ` (warehouse departure ${gate.activeEvents[0].departureText})` : ''}:
            </div>
          )}
          <ul className="pp-gate-list">
            {missing.map((i) => (
              <li key={i.code}>
                {i.text}
                {onGo && goLabel[i.section] && <> <button type="button" className="pp-link-btn" onClick={() => onGo(i.section)}>{goLabel[i.section]}</button></>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3l18 18" />
      <path d="M10.6 5.1A10.9 10.9 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.1M6.6 6.6C3.9 8.4 2 12 2 12s3.6 7 10 7c1.8 0 3.4-.5 4.8-1.3" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}

/** "Hyatt Place · 4 rooms"; "No overnight travel"; "No travel details added" when nothing is entered. */
export function tripText(sd) {
  const v = sd || {};
  if (v.overnightTravelRequired === 'no') return 'No overnight travel';
  const rooms = String(v.numberOfRooms || '').trim();
  const parts = [String(v.hotelName || '').trim() || (v.hotelAddress || v.teamHotelConf ? 'Hotel added' : ''),
    rooms ? `${rooms} room${rooms === '1' ? '' : 's'}` : ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No travel details added';
}

/** "Robert Hamm + 3": the supervisor (else the first person) and how many others. */
export function teamText(sd) {
  const v = sd || {};
  const names = [String(v.supervisor || '').trim()]
    .concat((Array.isArray(v.deliveryPersonnel) ? v.deliveryPersonnel : []).map((x) => String((x && x.name) || '').trim()))
    .filter(Boolean);
  if (!names.length) return 'No delivery team assigned';
  return names.length === 1 ? names[0] : `${names[0]} + ${names.length - 1}`;
}

// Review items that belong to a collapsed section; its summary says what they are.
const ISSUE_SECTION = {
  too_few_members: 'events',
  member_unresolved: 'events',
  venue_missing: 'locations',
  venue_tbd: 'locations',
  address_missing: 'locations',
  address_incomplete: 'locations',
  contact_missing: 'contacts',
  contact_phone_conflict: 'contacts',
  sales_lead_phone_conflict: 'contacts',
  member_folder_missing: 'resources'
};

/** "Nov 5–13", "Nov 28–Dec 3", "Dec 28, 2026–Jan 3, 2027", "Nov 5". */
function shortRange(a, b) {
  const p = (iso) => { const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? { y: +m[1], m: +m[2], d: +m[3] } : null; };
  const x = p(a);
  const y = p(b || a);
  if (!x || !y) return '';
  const mon = (q) => MONTHS[q.m - 1];
  if (a === b || !b) return `${mon(x)} ${x.d}`;
  if (x.y !== y.y) return `${mon(x)} ${x.d}, ${x.y}–${mon(y)} ${y.d}, ${y.y}`;
  if (x.m !== y.m) return `${mon(x)} ${x.d}–${mon(y)} ${y.d}`;
  return `${mon(x)} ${x.d}–${y.d}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function rowDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[+m[2] - 1]} ${+m[3]}`;
}

function formatWhen(iso) {
  if (!iso) return '';
  return formatDisplayInstant(iso) || iso;
}

function driveDownloadUrl(url) {
  if (!url) return '';
  const m = url.match(/\/d\/([A-Za-z0-9_-]+)/) || url.match(/[?&]id=([A-Za-z0-9_-]+)/);
  return m ? `https://drive.google.com/uc?export=download&id=${m[1]}` : '';
}

export default RunOfShowPanel;
